import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { base44 } from "@/api/base44Client";

const emptyForm = {
  slug: "",
  name: "",
  descriptor: "",
  system_prompt: "",
  consent_ref: "",
  source_ref: "",
  voice: "",
  is_active: false,
  retired_at: null,
  retired_reason: "",
  avatar_url: null,
};

function formValues(row) {
  return {
    ...row,
    slug: row.slug || "",
    name: row.name || "",
    descriptor: row.descriptor || "",
    system_prompt: row.system_prompt || "",
    consent_ref: row.consent_ref || "",
    source_ref: row.source_ref || "",
    voice: row.voice || "",
    is_active: Boolean(row.is_active),
    retired_at: row.retired_at || null,
    retired_reason: row.retired_reason || "",
    avatar_url: row.avatar_url || null,
  };
}

export default function PersonaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const creating = id === "new";
  const generation = useRef(0);
  const mounted = useRef(false);
  const busyRef = useRef(false);
  const profileDirtyRef = useRef(false);
  const corpusDirtyRef = useRef(false);
  const requestController = useRef(null);
  const [row, setRow] = useState(emptyForm);
  const [corpus, setCorpus] = useState("");
  const [question, setQuestion] = useState("");
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(!creating);
  const [busy, setBusy] = useState(false);
  const [refreshPending, setRefreshPending] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);
  const [corpusDirty, setCorpusDirty] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [retireReason, setRetireReason] = useState("");
  const [retireError, setRetireError] = useState("");
  const [corpusConfirmOpen, setCorpusConfirmOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const dirty = profileDirty || corpusDirty;
  const current = useCallback(
    (targetGeneration) =>
      mounted.current && generation.current === targetGeneration,
    [],
  );
  const setProfileDraftDirty = useCallback((value) => {
    profileDirtyRef.current = value;
    setProfileDirty(value);
  }, []);
  const setCorpusDraftDirty = useCallback((value) => {
    corpusDirtyRef.current = value;
    setCorpusDirty(value);
  }, []);

  const resetTransientState = useCallback(() => {
    setError("");
    setLoadError("");
    setNotice("");
    setPreview("");
    setRetireOpen(false);
    setRetireReason("");
    setRetireError("");
    setCorpusConfirmOpen(false);
    setLeaveOpen(false);
    setProfileDraftDirty(false);
    setCorpusDraftDirty(false);
    setRefreshPending(false);
    busyRef.current = false;
    setBusy(false);
  }, [setCorpusDraftDirty, setProfileDraftDirty]);

  const loadRow = useCallback(
    async (targetGeneration, { replaceDraft = false } = {}) => {
      if (creating) return null;
      requestController.current?.abort();
      const controller = new AbortController();
      requestController.current = controller;
      try {
        const next = await base44.personas.adminGet(id, {
          signal: controller.signal,
        });
        if (current(targetGeneration)) {
          if (replaceDraft || !profileDirtyRef.current)
            setRow(formValues(next));
          setLoading(false);
          setLoadError("");
          setRefreshPending(false);
        }
        return next;
      } catch (e) {
        if (current(targetGeneration) && e.name !== "AbortError") {
          setLoading(false);
          setRefreshPending(true);
          setLoadError(e.message || "Could not refresh this persona.");
        }
        return null;
      }
    },
    [creating, current, id],
  );

  useEffect(() => {
    mounted.current = true;
    const targetGeneration = ++generation.current;
    requestController.current?.abort();
    setRow(emptyForm);
    setCorpus("");
    setQuestion("");
    resetTransientState();
    setLoading(!creating);
    if (creating)
      return () => {
        mounted.current = false;
        generation.current += 1;
      };
    void loadRow(targetGeneration);
    return () => {
      mounted.current = false;
      generation.current += 1;
      requestController.current?.abort();
      requestController.current = null;
      busyRef.current = false;
    };
  }, [creating, id, loadRow, resetTransientState]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (
        !profileDirtyRef.current &&
        !corpusDirtyRef.current &&
        !busyRef.current
      )
        return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const setMutation = (value) => {
    busyRef.current = value;
    setBusy(value);
  };
  const change = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setRow((old) => ({ ...old, [key]: value }));
    setProfileDraftDirty(true);
    setError("");
    setNotice("");
  };
  const ready = !loading && !loadError && !refreshPending && !busy;

  function allowedPatch() {
    const patch = {
      name: row.name.trim(),
      descriptor: row.descriptor.trim(),
      consent_ref: row.consent_ref.trim(),
      source_ref: row.source_ref.trim(),
      voice: row.voice.trim(),
      is_active: Boolean(row.is_active),
    };
    if (creating) {
      patch.slug = row.slug.trim();
      if (row.system_prompt.trim())
        patch.system_prompt = row.system_prompt.trim();
    } else {
      if (!row.system_prompt.trim())
        throw new Error("System prompt is required when editing a persona.");
      patch.system_prompt = row.system_prompt.trim();
    }
    return patch;
  }

  async function refreshAfterCommit(targetGeneration) {
    const next = await loadRow(targetGeneration);
    if (!next && current(targetGeneration))
      setError(
        "The change was committed, but the latest server state could not be refreshed. Retry refresh before making another change.",
      );
    return next;
  }

  async function save(event) {
    event.preventDefault();
    if (!ready || busyRef.current) return;
    const targetGeneration = generation.current;
    let patch;
    try {
      patch = allowedPatch();
    } catch (e) {
      setError(e.message);
      return;
    }
    setMutation(true);
    setError("");
    setNotice("");
    try {
      const saved = creating
        ? await base44.personas.adminCreate({ ...patch, is_active: false })
        : await base44.personas.adminUpdate(id, patch);
      if (!current(targetGeneration)) return;
      setRow(formValues(saved));
      setProfileDraftDirty(false);
      setNotice(creating ? "Persona draft created." : "Persona changes saved.");
      if (creating) navigate(`/admin/personas/${saved.id}`);
      else await refreshAfterCommit(targetGeneration);
    } catch (e) {
      if (current(targetGeneration))
        setError(
          e.message || "Could not save persona. Your draft is still here.",
        );
    } finally {
      if (current(targetGeneration)) setMutation(false);
    }
  }

  async function replaceCorpus() {
    if (!ready || busyRef.current || !corpus.trim()) return;
    const targetGeneration = generation.current;
    const submitted = corpus;
    setCorpusConfirmOpen(false);
    setMutation(true);
    setError("");
    setNotice("");
    try {
      const result = await base44.personas.adminCorpus(id, submitted);
      if (!current(targetGeneration)) return;
      setCorpus("");
      setCorpusDraftDirty(false);
      if (result?.persona && !profileDirtyRef.current)
        setRow(formValues(result.persona));
      setNotice("Interview answers replaced successfully.");
      await refreshAfterCommit(targetGeneration);
    } catch (e) {
      if (current(targetGeneration))
        setError(
          e.message ||
            "Could not replace interview answers. Your corpus draft is still here.",
        );
    } finally {
      if (current(targetGeneration)) setMutation(false);
    }
  }

  async function runPreview() {
    if (!ready || busyRef.current || !question.trim()) return;
    const targetGeneration = generation.current;
    setMutation(true);
    setError("");
    setNotice("");
    setPreview("");
    try {
      const result = await base44.personas.adminPreview(id, question);
      if (current(targetGeneration)) {
        setPreview(result.text);
        setNotice("Preview completed.");
      }
    } catch (e) {
      if (current(targetGeneration))
        setError(e.message || "Could not run preview.");
    } finally {
      if (current(targetGeneration)) setMutation(false);
    }
  }

  async function retire() {
    if (!ready || busyRef.current || !retireReason.trim()) return;
    const targetGeneration = generation.current;
    setMutation(true);
    setRetireError("");
    setError("");
    setNotice("");
    try {
      const saved = await base44.personas.adminRetire(id, retireReason.trim());
      if (!current(targetGeneration)) return;
      setRow(formValues(saved));
      setProfileDraftDirty(false);
      setRetireOpen(false);
      setRetireReason("");
      setNotice("Persona retired.");
      await refreshAfterCommit(targetGeneration);
    } catch (e) {
      if (current(targetGeneration))
        setRetireError(e.message || "Could not retire persona.");
    } finally {
      if (current(targetGeneration)) setMutation(false);
    }
  }

  async function upload(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || creating || !ready || busyRef.current || row.retired_at)
      return;
    const targetGeneration = generation.current;
    setMutation(true);
    setError("");
    setNotice("");
    try {
      const result = await base44.personas.adminAvatar(id, file);
      if (!current(targetGeneration)) return;
      setNotice("Avatar uploaded.");
      if (result?.avatar_url)
        setRow((old) => ({ ...old, avatar_url: result.avatar_url }));
      await refreshAfterCommit(targetGeneration);
    } catch (e) {
      if (current(targetGeneration))
        setError(e.message || "Could not upload avatar.");
    } finally {
      if (current(targetGeneration)) setMutation(false);
    }
  }

  function leave(event) {
    if (busyRef.current || refreshPending) {
      event.preventDefault();
      return;
    }
    if (!dirty) return;
    event.preventDefault();
    setLeaveOpen(true);
  }

  const status = row.retired_at
    ? "Retired"
    : row.is_active
      ? "Active"
      : "Draft";
  const control =
    "mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground";
  const button =
    "min-h-11 rounded-lg px-4 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-50";

  if (loading && !row.name)
    return (
      <section className="persona-editor mx-auto max-w-4xl" aria-busy="true">
        <p role="status">Loading persona…</p>
      </section>
    );
  if (loadError && !row.name)
    return (
      <section className="persona-editor mx-auto max-w-4xl">
        <p className="mb-4 text-sm text-destructive" role="alert">
          {loadError}
        </p>
        <button
          type="button"
          className={`${button} bg-teal-700 text-white`}
          onClick={() => {
            setLoading(true);
            setLoadError("");
            void loadRow(generation.current);
          }}
        >
          Retry loading persona
        </button>
      </section>
    );

  return (
    <section
      className="persona-editor mx-auto max-w-4xl text-foreground"
      aria-label={creating ? "New persona" : `Edit ${row.name || "persona"}`}
    >
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link
            to="/admin/personas"
            onClick={leave}
            className="min-h-11 inline-flex items-center text-sm text-teal-700 dark:text-teal-300"
          >
            ← Personas
          </Link>
          <h1 className="text-3xl font-bold">
            {creating ? "New persona" : `Edit ${row.name || "persona"}`}
          </h1>
          {!creating && (
            <p className="mt-1 text-sm text-muted-foreground">
              Status: {status}
              {row.retired_reason ? ` — ${row.retired_reason}` : ""}
            </p>
          )}
        </div>
        {!creating && !row.retired_at && (
          <button
            type="button"
            onClick={() => {
              setRetireError("");
              setRetireOpen(true);
            }}
            disabled={!ready}
            className={`${button} border border-red-300 text-red-700 dark:text-red-300`}
          >
            Retire
          </button>
        )}
      </div>
      {error && (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p
          className="mb-4 text-sm text-emerald-700 dark:text-emerald-300"
          role="status"
        >
          {notice}
        </p>
      )}
      {refreshPending && (
        <p
          className="mb-4 flex flex-wrap items-center gap-3 text-sm text-amber-700 dark:text-amber-300"
          role="alert"
        >
          Server state needs refreshing before another action.
          <button
            type="button"
            className={`${button} border border-amber-700`}
            onClick={() => {
              setLoadError("");
              void loadRow(generation.current, { replaceDraft: !dirty });
            }}
          >
            Retry refresh
          </button>
        </p>
      )}
      <form
        onSubmit={save}
        className="space-y-4 rounded-2xl border border-border bg-background p-6 shadow-sm"
      >
        <fieldset disabled={!ready} className="space-y-4">
          <label className="block text-sm font-medium">
            Slug
            <input
              required
              disabled={!creating || !ready}
              maxLength={100}
              value={row.slug}
              onChange={change("slug")}
              className={control}
            />
          </label>
          <label className="block text-sm font-medium">
            Name
            <input
              required
              maxLength={200}
              value={row.name}
              onChange={change("name")}
              className={control}
            />
          </label>
          <label className="block text-sm font-medium">
            Descriptor
            <textarea
              maxLength={1000}
              value={row.descriptor}
              onChange={change("descriptor")}
              className={control}
            />
          </label>
          <label className="block text-sm font-medium">
            System prompt
            <textarea
              aria-label="System prompt"
              required={!creating}
              maxLength={100000}
              value={row.system_prompt}
              onChange={change("system_prompt")}
              rows={6}
              className={control}
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Leave blank on a new persona to use the default template.
            </span>
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium">
              Consent reference
              <input
                maxLength={500}
                value={row.consent_ref}
                onChange={change("consent_ref")}
                className={control}
              />
            </label>
            <label className="block text-sm font-medium">
              Source reference
              <input
                maxLength={500}
                value={row.source_ref}
                onChange={change("source_ref")}
                className={control}
              />
            </label>
          </div>
          <label className="block text-sm font-medium">
            Voice
            <input
              maxLength={100}
              value={row.voice}
              onChange={change("voice")}
              className={control}
            />
          </label>
          {!creating && !row.retired_at && (
            <label className="flex min-h-11 items-center gap-3 text-sm font-medium">
              <input
                type="checkbox"
                checked={row.is_active}
                onChange={change("is_active")}
                className="h-5 w-5"
              />
              Activate this persona for customers
              <span className="text-xs font-normal text-muted-foreground">
                (consent and source references are required)
              </span>
            </label>
          )}
          <button
            type="submit"
            disabled={!ready}
            className={`${button} bg-teal-700 text-white`}
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </fieldset>
        {!creating && (
          <div className="border-t border-border pt-4">
            <label className="block text-sm font-medium">
              Private avatar
              {row.avatar_url && (
                <>
                  <img data-decorative-image="true" src={base44.personas.adminAvatarUrl(id)} alt="Current persona avatar" className="my-2 h-20 w-20 rounded-full object-cover" />
                  <span className="text-only-alternative my-2">Current persona avatar hidden in text-only mode.</span>
                </>
              )}
              <input
                aria-label="Upload private avatar"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={!ready || !!row.retired_at}
                onChange={upload}
                className="mt-1 block min-h-11 max-w-full"
              />
            </label>
          </div>
        )}
      </form>
      {!creating && (
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <section className="rounded-2xl border border-border bg-background p-6 shadow-sm">
            <h2 className="mb-2 font-bold">Interview corpus</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Replacing the answers processes the new text first. If processing
              fails, the current answers stay available.
            </p>
            <label className="block text-sm font-medium">
              Consented interview answers
              <textarea
                aria-label="Consented interview answers"
                maxLength={200000}
                value={corpus}
                onChange={(e) => {
                  setCorpus(e.target.value);
                  setCorpusDraftDirty(true);
                }}
                rows={8}
                disabled={!ready || !!row.retired_at}
                className={control}
                placeholder="Paste consented interview answers"
              />
            </label>
            <button
              type="button"
              disabled={!ready || !!row.retired_at || !corpus.trim()}
              onClick={() => setCorpusConfirmOpen(true)}
              className={`${button} mt-3 bg-teal-700 text-white`}
            >
              Replace corpus
            </button>
          </section>
          <section className="rounded-2xl border border-border bg-background p-6 shadow-sm">
            <h2 className="mb-2 font-bold">Preview</h2>
            <label className="block text-sm font-medium">
              Test question
              <textarea
                aria-label="Test question"
                maxLength={6000}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                disabled={!ready || !!row.retired_at}
                className={control}
                placeholder="Ask a test question"
              />
            </label>
            <button
              type="button"
              disabled={!ready || !!row.retired_at || !question.trim()}
              onClick={runPreview}
              className={`${button} mt-3 border border-teal-700 text-teal-800 dark:text-teal-300`}
            >
              Run preview
            </button>
            {preview && (
              <p
                className="mt-4 max-h-80 overflow-y-auto whitespace-pre-wrap text-sm"
                aria-label="Preview result"
              >
                {preview}
              </p>
            )}
          </section>
        </div>
      )}
      <AlertDialog
        open={retireOpen}
        onOpenChange={(open) => !busy && setRetireOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire this persona?</AlertDialogTitle>
            <AlertDialogDescription>
              Customers will no longer see this persona. Existing interview
              answers remain stored for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="block text-sm font-medium">
            Reason for retirement
            <textarea
              aria-label="Reason for retirement"
              maxLength={500}
              value={retireReason}
              onChange={(e) => setRetireReason(e.target.value)}
              rows={3}
              disabled={busy}
              className={control}
            />
          </label>
          {retireError && (
            <p role="alert" className="text-sm text-destructive">
              {retireError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !retireReason.trim()}
              onClick={(event) => {
                event.preventDefault();
                void retire();
              }}
            >
              Retire persona
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={corpusConfirmOpen}
        onOpenChange={(open) => !busy && setCorpusConfirmOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace interview answers?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the current corpus after processing the new text.
              The replacement cannot be undone from this screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                void replaceCorpus();
              }}
            >
              Replace corpus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={leaveOpen}
        onOpenChange={(open) => !busy && setLeaveOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              Your persona edits or corpus text will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/admin/personas")}>
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
